import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import FiltersBar, { useFilterParams } from "../components/Filters";
import { JobCard, EmptyState, VEHICLE_LABELS, SERVICE_LABELS } from "../components/bits";
import { COUNTRIES } from "../constants";
import { List, Map as MapIcon } from "lucide-react";

const SORTS = [["pickup_date", "Pickup date"], ["price_asc", "Price ↑"], ["price_desc", "Price ↓"], ["distance", "Nearest"]];
const STATUSES = ["open", "assigned", "picked_up", "in_transit", "delivered", "disputed", "cancelled"];

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParam, clearAll] = useFilterParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState([]);
  const cardRefs = useRef({});

  const roleFields = user?.role === "courier"
    ? [["", "All jobs"], ["open", "Open"], ["mine", "Assigned to me"], ["bidded", "My bids"]]
    : user?.role === "company"
      ? [["", "All"], ["unassigned", "Unassigned"], ["assigned", "In progress"]]
      : [["", "All"], ["open", "Open"], ["assigned", "In progress"]];

  const fields = [
    { key: "q", label: "Ref, city or address", type: "text", short: "Search" },
    { key: "assignment", label: "Assignment", type: "select", options: roleFields },
    { key: "status", label: "Status", type: "select", options: STATUSES.map((s) => [s, s.replace(/_/g, " ")]) },
    { key: "country", label: "Country", type: "select", options: COUNTRIES },
    { key: "city", label: "Pickup city", type: "text", className: "w-28" },
    { key: "delivery_city", label: "Delivery city", type: "text", className: "w-28" },
    { key: "vehicle_type", label: "Vehicle", type: "select", options: Object.entries(VEHICLE_LABELS) },
    { key: "service_level", label: "Service", type: "select", options: Object.entries(SERVICE_LABELS) },
    { key: "date_from", label: "From date", type: "date", short: "From" },
    { key: "date_to", label: "To date", type: "date", short: "To" },
    { key: "near", label: "Near a place…", type: "near" },
    { key: "radius_km", label: "Radius km", type: "number", short: "Radius" },
    { key: "min_price", label: "Min €", type: "number", short: "Min" },
    { key: "max_price", label: "Max €", type: "number", short: "Max" },
    { key: "multi_stop", label: "Stops", type: "select", options: [["yes", "Multi-stop"], ["no", "Direct only"]] },
  ];

  const buildQuery = useCallback((pageNum) => {
    const p = Object.fromEntries(params.entries());
    const query = { page: pageNum, page_size: 50 };
    Object.entries(p).forEach(([k, v]) => {
      if (k === "multi_stop") query.multi_stop = v === "yes";
      else if (["min_price", "max_price", "radius_km", "near_lat", "near_lng"].includes(k)) query[k] = parseFloat(v);
      else if (k !== "near_label") query[k] = v;
    });
    return query;
  }, [params]);

  const load = useCallback(async (pageNum = 1, append = false) => {
    try {
      const { data } = await api.get("/jobs/marketplace", { params: buildQuery(pageNum) });
      setJobs((prev) => (append ? [...prev, ...data.jobs] : data.jobs));
      setTotal(data.total);
      setPage(pageNum);
    } catch { /* guard */ }
    setLoading(false);
  }, [buildQuery]);

  useEffect(() => { setLoading(true); load(1, false); }, [load]);
  useEffect(() => { const t = setInterval(() => load(1, false), 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (user) api.get("/saved-searches", { params: { context: "marketplace" } }).then((r) => setSaved(r.data.saved)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (selected && cardRefs.current[selected]) {
      cardRefs.current[selected].scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  if (!user) return null;

  const saveView = async () => {
    const name = window.prompt("Name this view:");
    if (!name) return;
    try {
      await api.post("/saved-searches", { name, context: "marketplace", params: Object.fromEntries(params.entries()) });
      const { data } = await api.get("/saved-searches", { params: { context: "marketplace" } });
      setSaved(data.saved);
      toast.success("View saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col" data-testid="marketplace-page">
      <Seo title="Job marketplace | CourierRadar" noindex />
      <FiltersBar fields={fields} params={params} setParam={setParam} clearAll={clearAll}
        sortOptions={SORTS} total={total} saved={saved} onSaveView={saveView}
        onApplySaved={(s) => setParam(Object.fromEntries(params.entries()) && Object.entries(s.params).reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})) || null}
        onDeleteSaved={async (s) => { await api.delete(`/saved-searches/${s.id}`); setSaved(saved.filter((x) => x.id !== s.id)); }} />

      <div className="flex-1 flex min-h-0 relative">
        <div className={`sm:w-[380px] lg:w-[420px] sm:border-r border-zinc-100 overflow-y-auto p-3 space-y-2.5 ${mobileView === "map" ? "hidden sm:block" : "w-full"}`} data-testid="job-list">
          {loading && <p className="text-xs text-zinc-400 text-center py-10">Loading jobs…</p>}
          {!loading && jobs.length === 0 && <EmptyState title="No jobs match" body="Adjust or reset the filters." />}
          {jobs.map((j) => (
            <div key={j.id} ref={(el) => (cardRefs.current[j.id] = el)}>
              <JobCard job={j} selected={selected === j.id}
                onClick={() => setSelected(j.id)}
                action={<span data-testid={`open-job-${j.ref}`} onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${j.id}`); }}
                  className="text-xs font-semibold text-black underline underline-offset-2">Open →</span>} />
              {j.distance_from_search_km != null && (
                <p className="text-[10px] text-zinc-400 pl-1 pt-0.5">{j.distance_from_search_km} km from search point</p>
              )}
            </div>
          ))}
          {jobs.length < total && (
            <button data-testid="load-more-btn" onClick={() => load(page + 1, true)}
              className="w-full border border-zinc-200 rounded-lg py-2.5 text-xs font-semibold hover:bg-zinc-50 transition-colors">
              Load more ({jobs.length} of {total})
            </button>
          )}
        </div>
        <div className={`flex-1 ${mobileView === "list" ? "hidden sm:block" : ""}`}>
          <MapView jobs={jobs} selectedId={selected} onSelectJob={(id) => { setSelected(id); setMobileView("list"); }} height="100%" />
        </div>
        <div className="sm:hidden absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1 bg-white border border-zinc-200 shadow-sm p-1 rounded-full">
          <button data-testid="mobile-list-view" onClick={() => setMobileView("list")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${mobileView === "list" ? "bg-black text-white" : ""}`}><List className="w-3.5 h-3.5" />List</button>
          <button data-testid="mobile-map-view" onClick={() => setMobileView("map")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${mobileView === "map" ? "bg-black text-white" : ""}`}><MapIcon className="w-3.5 h-3.5" />Map</button>
        </div>
      </div>
    </div>
  );
}
